import { useState } from 'react';
import { Community } from '@/mocks/communities';

interface FAQItem {
  question: string;
  answer: string;
}

interface CommunityFAQProps {
  community: Community;
}

function FAQRow({ item, index }: { item: FAQItem; index: number }) {
  const [open, setOpen] = useState(index === 0);

  return (
    <div className="border-b border-slate-100/70 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left cursor-pointer group"
      >
        <span className="text-sm font-semibold text-slate-700 group-hover:text-[#0088CC] transition-colors leading-snug">
          {item.question}
        </span>
        <span className={`w-6 h-6 flex items-center justify-center rounded-full border shrink-0 transition-all duration-200 ${
          open
            ? 'bg-[#0088CC] border-[#0088CC] text-white'
            : 'bg-white/60 border-slate-200/60 text-slate-400 group-hover:border-[#0088CC]/40 group-hover:text-[#0088CC]'
        }`}>
          <i className={`ri-${open ? 'subtract' : 'add'}-line text-xs`}></i>
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: open ? '300px' : '0px', opacity: open ? 1 : 0 }}
      >
        <p className="text-sm text-slate-500 leading-relaxed pb-4 pr-10">{item.answer}</p>
      </div>
    </div>
  );
}

export default function CommunityFAQ({ community }: CommunityFAQProps) {
  const faqs: FAQItem[] = [
    {
      question: `How do I join the ${community.name} Telegram community?`,
      answer: `You can join ${community.name} directly on Telegram by clicking the "Join Community" button on this page, or by visiting the Telegram link. The community is open to anyone interested in ${community.category.toLowerCase()} topics — no approval required.`,
    },
    {
      question: `How many members does ${community.name} have?`,
      answer: `${community.name} currently has ${community.memberCount.toLocaleString()} members, making it one of the ${community.activityLevel === 'high' ? 'most active' : 'notable'} Telegram communities in the ${community.category} category${community.region !== 'Global' ? ` (${community.region} region)` : ''}.`,
    },
    {
      question: `What topics does ${community.name} cover?`,
      answer: `${community.name} focuses on ${community.tags.join(', ')} and related topics. ${community.description}`,
    },
    {
      question: `How active is ${community.name}?`,
      answer: `${community.name} has a ${community.activityLevel} activity level${community.weeklyMessages ? `, with approximately ${community.weeklyMessages.toLocaleString()} messages exchanged per week` : ''}. The community was established on ${new Date(community.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.`,
    },
    {
      question: `What language is used in ${community.name}?`,
      answer: `${community.name} primarily communicates in ${community.language ?? 'English'}. The community is based in the ${community.region} region and welcomes members from around the world.`,
    },
    {
      question: `Is ${community.name} free to join?`,
      answer: `Yes, ${community.name} is completely free to join. Simply click the join link and you will be able to participate in all discussions and access community resources immediately — no subscription or payment required.`,
    },
  ];

  return (
    <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-white/80 p-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <span className="w-6 h-6 flex items-center justify-center text-[#0088CC]">
          <i className="ri-question-answer-line text-base"></i>
        </span>
        <h4 id="faq" className="text-lg font-bold text-slate-800">
          <a href="#faq" className="hover:text-[#0088CC] transition-colors">Frequently Asked Questions</a>
        </h4>
      </div>
      <p className="text-xs text-slate-400 mb-5 ml-8">Common questions about {community.name}</p>

      {/* FAQ list */}
      <div className="divide-y divide-slate-100/70">
        {faqs.map((item, i) => (
          <FAQRow key={i} item={item} index={i} />
        ))}
      </div>
    </div>
  );
}
